<template>
  <div class="verification-step">
    <div class="verification-content">
      <p class="verification-message">
        We sent a code to the following email:<br>
        <span class="email-highlight">{{ userEmail }}</span>
      </p>
      
      <div class="form-group">
        <label for="verificationCode">Enter code</label>
        <input 
          type="text"
          id="verificationCode"
          v-model="verificationCode"
          class="form-control"
          placeholder="Enter Code"
        >
        <div v-if="error" class="error-message">{{ error }}</div>
      </div>
      
      <!-- Feedback Card Section -->
      <div v-if="feedbackMessage" class="feedback-card" :class="feedbackType">
        <div class="feedback-icon">
          <v-icon v-if="feedbackType === 'success'">mdi-check-circle</v-icon>
          <v-icon v-else-if="feedbackType === 'error'">mdi-alert-circle</v-icon>
          <v-icon v-else-if="feedbackType === 'warning'">mdi-alert</v-icon>
          <v-icon v-else>mdi-information</v-icon>
        </div>
        <div class="feedback-content">
          <div class="feedback-title">
            {{ feedbackTitle }}
          </div>
          <div class="feedback-message">
            {{ feedbackMessage }}
          </div>
        </div>
      </div>
      
      <div class="action-buttons">
        <button class="btn btn-cancel" @click="handleCancel">Cancel</button>
        <button 
          class="btn btn-submit"
          @click="handleSubmit"
          :disabled="isSubmitting || !verificationCode"
        >
          <template v-if="isSubmitting">
            <v-progress-circular indeterminate color="white" size="20" class="spinner" />
          </template>
          <template v-else>
            Submit
          </template>
        </button>
      </div>
      
      <div class="verification-links">
        <a 
          href="#" 
          class="verification-link"
          :class="{ disabled: isRequestingNewCode }"
          @click.prevent="!isRequestingNewCode && requestNewCode()"
        >
          <template v-if="isRequestingNewCode">
            <v-progress-circular indeterminate color="primary" size="16" class="inline-spinner" />
            Sending...
          </template>
          <template v-else>
            Request another code
          </template>
        </a>
        <a href="#" class="verification-link" @click.prevent="goBackToRegistration">
          Back to registration
        </a>
      </div>
    </div>
  </div>
</template>

<script>
import apiService from '@/services/apiService'; // Import apiService
import { saveAuthToken } from '@/utils/auth';

export default {
  name: 'RegisterStep2',
  props: {
    formData: {
      type: Object,
      required: true
    }
  },
  data() {
    return {
      verificationCode: '',
      isSubmitting: false,
      isRequestingNewCode: false,
      error: '',
      generalError: '',
      feedbackMessage: '',
      feedbackType: '', // 'success', 'error', 'warning', 'info'
      feedbackTitle: ''
    };
  },
  computed: {
    userEmail() {
      // Get email from parent formData passed down
      return this.formData.email;
    }
  },
  methods: {
    showFeedback(message, type = 'info', title = '') {
      this.feedbackMessage = message;
      this.feedbackType = type;
      this.feedbackTitle = title || this.getDefaultTitle(type);
    },
    clearFeedback() {
      this.feedbackMessage = '';
      this.feedbackType = '';
      this.feedbackTitle = '';
    },
    getDefaultTitle(type) {
      switch (type) {
        case 'success': return 'Success';
        case 'error': return 'Error';
        case 'warning': return 'Warning';
        default: return 'Information';
      }
    },
    handleCancel() {
      // Navigate back to Step 1
      this.$emit('prev-step');
    },
    async handleSubmit() { // Make async
      console.log('handleSubmit called - Beginning of method');
      
      if (!this.verificationCode) {
        this.error = 'Please enter the verification code';
        return;
      }

      this.isSubmitting = true;
      this.error = '';
      this.generalError = '';
      this.clearFeedback();

      const payload = {
        email: this.userEmail,
        code: this.verificationCode
      };
      
      console.log('Verification payload prepared:', payload);
      
      if (!payload.email) {
          console.error('Email is missing in payload for verification!');
          this.error = 'An error occurred: Email address not found.';
          this.isSubmitting = false;
          return;
      }

      console.log('About to call apiService.verifyEmail...');
      
      try {
        console.log('Inside try block - before API call');
        
        // Check if apiService and its methods are available
        if (!apiService) {
          console.error('apiService is not defined!');
          this.error = 'Internal error: API service unavailable';
          this.isSubmitting = false;
          return;
        }
        
        if (typeof apiService.verifyEmail !== 'function') {
          console.error('apiService.verifyEmail is not a function!', apiService);
          this.error = 'Internal error: API method unavailable';
          this.isSubmitting = false;
          return;
        }
        
        // Try calling the API with a direct URL in case there's an issue with the configured endpoint
        try {
          // First attempt with the configured service
          console.log('Calling apiService.verifyEmail with payload:', payload);
          const response = await apiService.verifyEmail(payload);
          console.log('Email Verification Success:', response.data);
        
          // Update token if provided in response
          if (response.data.token) {
            console.log('Token received in VerifyEmail step:', response.data.token);
            console.log('localStorage authToken BEFORE VerifyEmail save:', localStorage.getItem('authToken'));
            saveAuthToken(response.data.token);
            this.$store.commit('SET_TOKEN', response.data.token);
            console.log('localStorage authToken AFTER VerifyEmail save:', localStorage.getItem('authToken'));
          } else {
            console.warn('No token received in VerifyEmail response.');
          }
        
          // No need to update formData with verification code itself
          // Just proceed to the next step (password creation)
          this.$emit('next-step');
        } catch (innerError) {
          console.error('First attempt failed, trying direct axios call:', innerError);
          
          // If configured service fails, try direct axios import as a fallback
          console.log('Trying direct axios call as fallback...');
          
          const axios = await import('axios');
          console.log('Axios imported:', axios);
          
          try {
            const directResponse = await axios.default.post(
              `${process.env.VUE_APP_API_BASE_URL}/business/verify-email/`,
              payload,
              {
                headers: {
                  'Content-Type': 'application/json',
                  'Accept': 'application/json',
                },
                timeout: 10000
              }
            );
            console.log('Direct axios call succeeded:', directResponse.data);
            if (directResponse.data.token) {
              console.log('Token received in fallback VerifyEmail call:', directResponse.data.token);
              console.log('localStorage authToken BEFORE fallback VerifyEmail save:', localStorage.getItem('authToken'));
              saveAuthToken(directResponse.data.token);
              this.$store.commit('SET_TOKEN', directResponse.data.token);
              console.log('localStorage authToken AFTER fallback VerifyEmail save:', localStorage.getItem('authToken'));
            } else {
              console.warn('No token received in fallback VerifyEmail response.');
            }
            this.$emit('next-step');
          } catch (directError) {
            console.error('Direct axios call failed:', directError);
            throw directError; // Let the outer catch block handle it
          }
        }
      } catch (error) {
        console.error('ALL verification attempts failed:', error);
        console.error('Error details:', error.response || error.message);
        
        if (error.response && error.response.data) {
          const data = error.response.data;

          if (data.errors && typeof data.errors === 'object') {
            // Reset errors
            this.error = '';
            for (const [field, messages] of Object.entries(data.errors)) {
              const msg = Array.isArray(messages) ? messages.join(', ') : messages;
              if (['code', 'verification_code'].includes(field)) {
                this.error = msg;
              } else {
                this.showFeedback(msg, 'error');
              }
            }
          } else if (data.detail) {
            this.showFeedback(data.detail, 'error');
          } else {
            this.showFeedback('Email verification failed. Please try again.', 'error');
          }
        } else {
          this.showFeedback('Network error. Please try again.', 'error');
        }
      } finally {
        this.isSubmitting = false;
      }
    },
    async requestNewCode() {
      // Prevent multiple simultaneous requests
      if (this.isRequestingNewCode) return;
      
      this.isRequestingNewCode = true;
      // Clear any existing errors and feedback
      this.error = '';
      this.generalError = '';
      this.clearFeedback();
      
      if (!this.userEmail) {
        this.showFeedback('Email address not found. Please go back and try again.', 'error');
        this.isRequestingNewCode = false;
        return;
      }
      
      try {
        const payload = { email: this.userEmail };
        console.log('Requesting new verification code for:', this.userEmail);
        
        // Try the API service first
        try {
          const response = await apiService.post('/business/resend-verification-code/', payload);
          console.log('Resend verification code success:', response.data);
          
          // Show success message
          this.showFeedback(
            'A new verification code has been sent to your email address. Please check your inbox.',
            'success',
            'Code Sent'
          );
          
        } catch (serviceError) {
          console.warn('API service failed, trying direct axios call:', serviceError);
          
          // Fallback to direct axios call
          const axios = await import('axios');
          const directResponse = await axios.default.post(
            `${process.env.VUE_APP_API_BASE_URL}/business/resend-verification-code/`,
            payload,
            {
              headers: {
                'Content-Type': 'application/json',
                'Accept': 'application/json',
              },
              timeout: 10000
            }
          );
          console.log('Direct axios resend success:', directResponse.data);
          
          // Show success message
          this.showFeedback(
            'A new verification code has been sent to your email address. Please check your inbox.',
            'success',
            'Code Sent'
          );
        }
        
      } catch (error) {
        console.error('Failed to request new verification code:', error);
        
        if (error.response && error.response.data) {
          const data = error.response.data;
          
          if (data.detail) {
            // Handle specific error messages from backend
            if (error.response.status === 429) {
              this.showFeedback(data.detail, 'warning', 'Rate Limit');
            } else if (error.response.status === 400) {
              this.showFeedback(data.detail, 'warning', 'Validation Error');
            } else if (error.response.status === 404) {
              this.showFeedback('User not found. Please go back and register again.', 'error', 'User Not Found');
            } else {
              this.showFeedback(data.detail, 'error');
            }
          } else {
            this.showFeedback('Please, wait 30 seconds to try again.', 'error');
          }
        } else {
          this.showFeedback('Network error. Please check your connection and try again.', 'error', 'Connection Error');
        }
      } finally {
        this.isRequestingNewCode = false;
      }
    },
    goBackToRegistration() {
      // Alias for handleCancel, navigate back to Step 1
      this.handleCancel();
    }
  }
};
</script>

<style scoped>
.verification-step {
  width: 100%;
  background: white;
  border-radius: 8px;
  overflow: hidden;
}

.verification-content {
  padding: 30px;
}

.verification-message {
  text-align: center;
  margin-bottom: 25px;
  color: #333;
  font-size: 16px;
}

.email-highlight {
  font-weight: bold;
  color: #000;
}

.form-group {
  margin-bottom: 25px;
}

label {
  display: block;
  font-size: 14px;
  margin-bottom: 10px;
  color: #333;
}

.form-control {
  width: 100%;
  padding: 12px 15px;
  font-size: 16px;
  border: 1px solid #ddd;
  border-radius: 4px;
  background-color: #fff;
}

.error-message {
  color: #e94057;
  font-size: 12px;
  margin-top: 5px;
}

.action-buttons {
  display: flex;
  gap: 15px;
  margin-bottom: 25px;
}

.btn {
  flex: 1;
  padding: 12px 0;
  font-size: 16px;
  border-radius: 4px;
  cursor: pointer;
  border: 1px solid transparent;
  text-align: center;
}

.btn-cancel {
  background-color: #fff;
  color: #333;
  border-color: #ddd;
}

.btn-cancel:hover {
  background-color: #f1f1f1;
}

.btn-submit {
  background-color: #4A6282;
  color: white;
}

.btn-submit:hover {
  background-color: #3d5270;
}

.btn-submit:disabled {
  background-color: #cccccc;
  cursor: not-allowed;
}

.verification-links {
  display: flex;
  justify-content: space-between;
  font-size: 14px;
  margin-top: 10px;
}

.verification-link {
  color: #4A6282;
  text-decoration: none;
}

.verification-link:hover {
  text-decoration: underline;
}

.verification-link.disabled {
  color: #999;
  cursor: not-allowed;
  pointer-events: none;
}

.inline-spinner {
  margin-right: 5px;
  vertical-align: middle;
}

.general-error {
  text-align: center;
}

.feedback-card {
  display: flex;
  align-items: flex-start;
  padding: 16px;
  margin: 20px 0;
  border-radius: 8px;
  border-left: 4px solid;
  background-color: #f8f9fa;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.feedback-card.success {
  border-left-color: #28a745;
  background-color: #d4edda;
}

.feedback-card.error {
  border-left-color: #dc3545;
  background-color: #f8d7da;
}

.feedback-card.warning {
  border-left-color: #ffc107;
  background-color: #fff3cd;
}

.feedback-card.info {
  border-left-color: #17a2b8;
  background-color: #d1ecf1;
}

.feedback-icon {
  margin-right: 12px;
  margin-top: 2px;
}

.feedback-card.success .feedback-icon {
  color: #155724;
}

.feedback-card.error .feedback-icon {
  color: #721c24;
}

.feedback-card.warning .feedback-icon {
  color: #856404;
}

.feedback-card.info .feedback-icon {
  color: #0c5460;
}

.feedback-content {
  flex: 1;
}

.feedback-title {
  font-weight: 600;
  margin-bottom: 4px;
  font-size: 14px;
}

.feedback-card.success .feedback-title {
  color: #155724;
}

.feedback-card.error .feedback-title {
  color: #721c24;
}

.feedback-card.warning .feedback-title {
  color: #856404;
}

.feedback-card.info .feedback-title {
  color: #0c5460;
}

.feedback-message {
  font-size: 14px;
  line-height: 1.4;
}

.feedback-card.success .feedback-message {
  color: #155724;
}

.feedback-card.error .feedback-message {
  color: #721c24;
}

.feedback-card.warning .feedback-message {
  color: #856404;
}

.feedback-card.info .feedback-message {
  color: #0c5460;
}

.spinner {
  margin: 0;
}
</style> 